<template>
  <div>
    <div class="d-flex">
      <div class="flex-grow-1 d-flex align-center" data-cy="podFiles-mediaAssets-title">
        <h1>{{ $t('routes.pod.mediaAssets') }}</h1>
      </div>

      <div v-if="$canUploadFileAndAsset() && getCurrentNode" class="flex-grow-0 d-flex align-center">
        <UploadFilesDialog
          v-model="uploadAssetsDialogVisible"
          :node="getCurrentNode"
          :pod="pod"
          :uploadDialogType="'assets'"
        >
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props"
                   color="primary"
                   class="mr-1"
                   elevation="0"
                   data-cy="podFiles-mediaAssets-uploadAsset-btn">
              <v-icon>mdi-plus-circle</v-icon>
              {{ $t('routes.pod.menuTools.uploadAsset') }}
            </v-btn>
          </template>
        </UploadFilesDialog>
      </div>
      <div class="d-flex align-center" v-if="hasAssets">
        <v-menu v-model="assetMenuIsActive" :close-on-content-click="true" style="z-index: 1100;">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              width="36"
              height="36"
              rounded="circle"
              data-cy="podFiles-mediaAssets-assetMenu-btn"
              icon
              :aria-label="$t('common.options')"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item v-if="!$isAdobeExtension() && !$isFCPExtension()" @click="downloadAllAssets">
              <template v-slot:prepend>
                <v-icon size="small">mdi-folder-arrow-down-outline</v-icon>
              </template>
              <v-list-item-title>{{ $t('routes.pod.downloadAllAssets') }}</v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="!$isAdobeExtension() && !$isFCPExtension()"
              @click="downloadSelectedAssets"
              :disabled="!hasSelectedAssets"
              v-show="hasSelectedAssets"
            >
              <template v-slot:prepend>
                <v-icon size="small">mdi-file-download-outline</v-icon>
              </template>
              <v-list-item-title>{{ $t('routes.pod.downloadSelectedAssets') }}</v-list-item-title>
            </v-list-item>

            <DeleteAllAssetsDialog :pod="pod" v-if="$canDeleteNodeAndPod()">
              <template v-slot:activator="{ props }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <v-icon size="small">mdi-delete-forever-outline</v-icon>
                  </template>
                  <v-list-item-title>{{ $t('routes.pod.deleteAllAssets') }}</v-list-item-title>
                </v-list-item>
              </template>
            </DeleteAllAssetsDialog>

            <DeleteSelectedAssetsDialog :pod="pod" @assetsDeleted="closeMenu" v-if="$canDeleteNodeAndPod()">
              <template v-slot:activator="{ props }">
                <v-list-item v-bind="props" :disabled="!hasSelectedAssets"  v-show="hasSelectedAssets">
                  <template v-slot:prepend>
                    <v-icon size="small">mdi-trash-can-outline</v-icon>
                  </template>
                  <v-list-item-title>{{ $t('routes.pod.deleteSelectedAssets') }}</v-list-item-title>
                </v-list-item>
              </template>
            </DeleteSelectedAssetsDialog>
          </v-list>
        </v-menu>
      </div>
    </div>

    <div>
      <div v-for="item in items" :key="item.id" role="list" >
        <div class="my-2 text-center" aria-hidden="true">
          <h3>{{ $t(item.name) }}</h3>
        </div>

        <div
          v-for="asset in item.children"
          :key="asset.id"
          role="listitem"
          :aria-label="getAssetRowLabel(asset)"
          v-on:click="toggleAssetSelection(asset)"
          v-on:contextmenu.stop.prevent="contextMenuClick($event, asset)"
          v-on:keydown="handleAssetRowKeydown($event, asset)"
          class="prevent-select mt-1 d-flex align-center pod-asset-list-item"
        >
          <div v-if="asset.uuid" class="d-flex align-center justify-center flex-grow-0">
            <v-checkbox
              v-bind:model-value="isCheckboxSelected(asset)"
              v-on:click.stop.prevent="toggleAssetSelection(asset)"
              class="mr-1"
              density="comfortable"
              width="32"
              direction="vertical"
              data-cy="podFiles-mediaAssets-checkbox"
              hide-details
              :aria-label="getAssetSelectLabel(asset)"
            />
          </div>

          <!-- Asset name -->
          <div class="flex-grow-1">

            <div class="d-flex">
              <div class="d-flex align-center mr-2">
                <v-icon v-if="asset.broken">
                  mdi-image-broken
                </v-icon>

                <v-tooltip
                  v-if="isAudioDescriptionAsset(asset)"
                  location="bottom"
                  :aria-label="$t('menus.uploadFiles.assetTypeAudioDescription')"
                >
                  <template v-slot:activator="{ props }">
                    <span
                      v-bind="props"
                      class="audio-description-icon"
                      role="img"
                      :aria-label="$t('menus.uploadFiles.assetTypeAudioDescription')"
                    ></span>
                  </template>
                  <span>{{ $t('menus.uploadFiles.assetTypeAudioDescription') }}</span>
                </v-tooltip>

                <v-tooltip v-if="!asset.broken && isSystemGeneratedFile(asset)" location="bottom" :aria-label="$t('routes.pod.thisIsSystemFile')">
                  <template v-slot:activator="{ props }">
                    <img
                      v-bind="props"
                      :src="getSystemGeneratedIcon"
                      style="width: 20px; height: 20px;"
                      alt=""
                      aria-hidden="true"
                    />
                  </template>
                  <span>{{ $t('routes.pod.thisIsSystemFile') }}</span>
                </v-tooltip>
              </div>
              <div class="flex-grow-1" data-cy="podFiles-mediaAssets-assetName">
                <span style="word-wrap: anywhere; white-space: pre-line; font-weight: 600;">
                  {{ asset.name }}
                </span>

                <!-- Status or clarification -->
                <div v-if="asset.clarification != null"
                     data-cy="podFiles-mediaAssets-clarification">
                  <v-tooltip location="bottom" :aria-label="asset.clarification">
                    <template v-slot:activator="{ props }">
                      <small v-bind="props">{{ assetState(asset) }}</small>
                    </template>
                    <span> {{ asset.clarification }} </span>
                  </v-tooltip>
                </div>
                <div v-else data-cy="podFiles-mediaAssets-state">
                  <small>{{ assetState(asset) }}</small>
                </div>

              </div>
            </div>

          </div>


          <!-- Size -->
          <div class="text-center">
            <small>{{ formatSize((asset && asset.size) ? asset.size : 0, 2) }}</small>
            <br/>
            <!-- Mimetype -->
            <small>{{ humanizeMimeType(asset) }}</small>
          </div>


          <!-- Three-dot menu -->
          <div class="ml-2">
            <v-btn
              @click.stop="contextMenuBtnClick($event, asset)"
              variant="text"
              rounded="circle"
              width="36"
              height="36"
              icon
              data-cy="podFiles-mediaAssets-actions-btn"
              :aria-label="getAssetMenuLabel(asset)"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <AssetsContextMenu
        v-if="contextMenuAsset"
        v-model="showingAssetContextMenu"
        :positionX="contextMenuX"
        :positionY="contextMenuY"
        :pod="pod"
        :asset="contextMenuAsset"
      />

    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useMainStore } from '@/stores/MainStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { usePodStore } from '@/stores/PodStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import MimetypeHelper from '@/components/MimetypeHelper.js';

import AssetsContextMenu from './AssetsContextMenu.vue';
import UploadFilesDialog from '@/routes/nodes2/components/dialogs/UploadFilesDialog.vue';
import DeleteSelectedAssetsDialog from './dialogs/DeleteSelectedAssetsDialog.vue';
import DeleteAllAssetsDialog from './dialogs/DeleteAllAssetsDialog.vue';

import SmallTWDark from '@/assets/images/smalltw-dark.png';
import SmallTWLight from '@/assets/images/smalltw-light.png';
import { formatSize } from '@/components/formatters.js';

const mappedFileMimeTypes = new Map([
  ['docx', 'application/word'],
  ['pdf', 'application/pdf'],
]);

export default {
  name: "AssetsToolbar",
  components: {
    AssetsContextMenu,
    UploadFilesDialog,
    DeleteSelectedAssetsDialog,
    DeleteAllAssetsDialog,
  },
  data: function() {
    return {
      assetMenuIsActive: false,
      showingAssetContextMenu: false,
      contextMenuAsset: null,
      contextMenuX: 0,
      contextMenuY: 0,
      uploadAssetsDialogVisible: false,
    };
  },
  methods: {
    formatSize,
    humanizeMimeType: function (asset) {
      return mappedFileMimeTypes.has(asset.filetype)
        ? mappedFileMimeTypes.get(asset.filetype)
        : asset.mimetype;
    },
    filterByType: function(availableTypes, mimetypeFilter) {
      const JOINTTYPES = [
        this.assetTypes.WEB, this.assetTypes.EXTERNAL, this.assetTypes.OTHER
      ];

      return (x) => {
        if (mimetypeFilter && JOINTTYPES.indexOf(x.type) >= 0 && mimetypeFilter(x)) {
          return true;
        }
        return availableTypes.indexOf(x.type) >= 0;
      };
    },
    filterWithExclusion: function(availableTypes) {
      const DISJOINTTYPES = [
        this.assetTypes.WEB, this.assetTypes.EXTERNAL, this.assetTypes.OTHER
      ];

      return (x) => {
        if (
          DISJOINTTYPES.indexOf(x.type) >= 0 &&
          (
            MimetypeHelper.isVideoAsset(x) ||
            MimetypeHelper.isAudioAsset(x) ||
            MimetypeHelper.isImageAsset(x)
          )
        ) {
          return false;
        }

        return availableTypes.indexOf(x.type) >= 0;
      }
    },
    toggleAssetSelection: function(item) {
      const podStore = usePodStore();
      podStore.toggleSelectedAsset(item);
    },
    handleAssetRowKeydown(event, asset) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggleAssetSelection(asset);
        return;
      }

      if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
        event.preventDefault();
        this.contextMenuFromElement(asset, event.currentTarget);
      }
    },
    downloadAllAssets() {
      let payload = {
        nodeids: [],
        podids: [this.pod.id],
        assetids: [],
        excludeAssets: false,
      };

      const authStore = useAuthStore();
      authStore.getCurrentSession()
          .then(function(session) {
            payload['id-token'] = session.tokens.idToken.toString();
            const commonStore = useCommonStore();
            return commonStore.downloadThings(payload);
          });
    },
    downloadSelectedAssets() {
      // console.log('this.selectedAssets', this.selectedAssets);
      if (this.selectedAssets.length === 0) {
        return;
      }

      const selectedAssetIds = this.selectedAssets.map((x) => x.id);
      let payload = {
        podids: [this.pod.id],
        assetids: selectedAssetIds,
      };

      const authStore = useAuthStore();
      authStore.getCurrentSession()
          .then(function(session) {
            payload['id-token'] = session.tokens.idToken.toString();
            const commonStore = useCommonStore();
            return commonStore.downloadThings(payload);
          });
    },
    stub(value) {
      console.log(value);
    },
    closeMenu() {
      this.assetMenuIsActive = false;
    },
    contextMenuClick(event, asset) {
      this.showingAssetContextMenu = false;

      this.contextMenuX = event.clientX;
      this.contextMenuY = event.clientY;
      this.contextMenuAsset = asset;

      this.$nextTick(() => {
        this.showingAssetContextMenu = true;
      });
    },
    contextMenuBtnClick(event, asset) {
      this.contextMenuFromElement(asset, event.currentTarget);
    },
    contextMenuFromElement(asset, element) {
      this.showingAssetContextMenu = false;

      const rect = element?.getBoundingClientRect();
      this.contextMenuX = rect ? rect.right : 0;
      this.contextMenuY = rect ? rect.bottom : 0;
      this.contextMenuAsset = asset;

      this.$nextTick(() => {
        this.showingAssetContextMenu = true;
      });
    },
    getAssetSelectLabel(asset) {
      return `${this.$t('routes.pod.menuVersions.selectPod')} ${asset.name}`;
    },
    getAssetRowLabel(asset) {
      return `${asset.name}. ${this.assetState(asset)}. ${this.formatSize((asset && asset.size) ? asset.size : 0, 2)}. ${this.humanizeMimeType(asset)}.`;
    },
    getAssetMenuLabel(asset) {
      return `${this.$t('common.options')} ${asset.name}`;
    },
    isAudioDescriptionAsset(asset) {
      return asset.type === this.assetTypes.AUDIODESCRIPTION;
    },
  },
  computed: {
    getDarkMode() {
      const mainStore = useMainStore();
      return mainStore.getDarkMode;
    },
    pod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    getCurrentNode: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    assetTypes: function() {
      const commonStore = useCommonStore();
      return commonStore.assetTypes;
    },
    stackedAssets: function() {
      let result = [];

      if (!this.pod || !this.pod.asset) {
        return result;
      }

      let keys = Object.getOwnPropertyNames(this.pod.asset);

      for(let i = 0; i < keys.length; i++) {
        result = result.concat(this.pod.asset[keys[i]]);
      }

      return result;
    },
    items: function() {
      let mainAssets = [this.assetTypes.RAW];
      let videoAssets = [this.assetTypes.VIDEO];
      let audioAssets = [this.assetTypes.AUDIO, this.assetTypes.AUDIODESCRIPTION];
      let imageAssets = [this.assetTypes.THUMBNAIL, this.assetTypes.LIVEPREVIEW, this.assetTypes.FRAME, this.assetTypes.IMAGE];
      let subsAssets = [this.assetTypes.VTT, this.assetTypes.SRT];
      let otherAssets = [this.assetTypes.EXTERNAL, this.assetTypes.SPEECHTOTEXT, this.assetTypes.OTHER];

      // this.assetTypes.WEB is filtered by mimetype

      let treeArray = [
        { id: "str1", name: "routes.pod.assetRaw", children: this.stackedAssets.filter(this.filterByType(mainAssets))},
        { id: "str2", name: "routes.pod.assetVideo", children: this.stackedAssets.filter(this.filterByType(videoAssets, MimetypeHelper.isVideoAsset))},
        { id: "str3", name: "routes.pod.assetAudio", children: this.stackedAssets.filter(this.filterByType(audioAssets, MimetypeHelper.isAudioAsset))},
        { id: "str4", name: "routes.pod.assetImage", children: this.stackedAssets.filter(this.filterByType(imageAssets, MimetypeHelper.isImageAsset))},
        { id: "str5", name: "routes.pod.assetSubtitle", children: this.stackedAssets.filter(this.filterByType(subsAssets))},
        { id: "str6", name: "routes.pod.assetOther", children: this.stackedAssets.filter(this.filterWithExclusion(otherAssets))},
      ];

      // truncate empty tree items
      return treeArray.filter(x => x.children.length > 0);
    },
    podStates: function() {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    assetState: function() {
      const states = this.podStates;

      let self = this;
      return (item) => {
        if (item.broken) {
          return self.$t('routes.pod.brokenAsset');
        }
        switch (item.state) {
          case states.ERROR: return self.$t('routes.pod.stateError');
          case states.PENDING: return self.$t('routes.pod.statePending');
          case states.PROCESSING: return self.$t('routes.pod.stateProcessing');
          case states.COMPLETE: return self.$t('routes.pod.stateComplete');
        }
      }
    },
    isCheckboxSelected: function() {
      return (item) => {
        const podStore = usePodStore();
        const assets = podStore.getSelectedAssets;

        return assets.indexOf(item) >= 0;
      };
    },
    isSystemGeneratedFile: function() {
      const systemTypes = [
        this.assetTypes.THUMBNAIL, this.assetTypes.LIVEPREVIEW,
        this.assetTypes.FRAME, this.assetTypes.RAW,
        this.assetTypes.AUDIO, this.assetTypes.WEB,
        this.assetTypes.IMAGE, this.assetTypes.VIDEO,
        // this.assetTypes.VTT, this.assetTypes.SRT,
        this.assetTypes.SPEECHTOTEXT, this.assetTypes.OTHER
      ];
      return (item) => {
        return systemTypes.indexOf(item.type) >= 0;
      }
    },
    selectedAssets: function() {
      const podStore = usePodStore();
      return podStore.getSelectedAssets;
    },
    hasSelectedAssets: function() {
      return this.selectedAssets.length > 0;
    },
    getSystemGeneratedIcon: function() {
      if (this.getDarkMode) {
        return SmallTWDark;
      }

      return SmallTWLight;
    },
    hasAssets: function() {
      return this?.stackedAssets && this.stackedAssets.length > 0;
    },
  }
}
</script>

<style lang="scss" scoped>
.pod-asset-list-item {
  border-radius: 10px;
  padding: 8px 4px;
  cursor: pointer;
}

.pod-asset-list-item:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.audio-description-icon {
  display: block;
  width: 20px;
  height: 20px;
  background-color: var(--tw-icons-colors-default);
  -webkit-mask: url('@/assets/images/audio-description.svg') no-repeat center / contain;
  mask: url('@/assets/images/audio-description.svg') no-repeat center / contain;
}

.v-theme--light .pod-asset-list-item {
  background-color: #e6ecf1;
}

.v-theme--dark .pod-asset-list-item {
  background-color: #2f3640;
}
</style>
